/**
 * WerkplekProfielInline
 *
 * De inhoud van de "Werkplek"-tegel op het teamdashboard. Gebruikt het GEDRAGSMODEL (werkplekmix → kern/steun/aanvullend)
 * in plaats van de energie-gewogen percentages, maar behoudt bewust de
 * redactionele LOOK & FEEL van het vroegere TeamWorkplaceNeeds:
 *   - Hero: de sterkste behoefte als statement (sage)
 *   - Secundaire lijst: overige kern/steun-behoeften in 2 kolommen
 *   - Subtiele afsluiting: aanvullende behoeften ("minder vraag")
 *   - Afsluitend: gedragssignaal ("let op de balans"), secundair
 *
 * SCOPE — bewust ALLEEN behoefte, gebruik en gedrag:
 *   GEEN m², aantallen, percentages of inrichtingsadvies.
 *
 * NIEUW t.o.v. dat vroegere blok: per type tonen we de Ruimteboek-bouwstenen
 * (voorbeeldplekken) als verduidelijking van wat dit SOORT plek kan zijn.
 */

import React from 'react';
import { SPACING, TYPE, useIsMobile } from '../../ui/tokens';

import {
    buildWerkplekProfiel,
    buildGedragssignalen,
    aantallenUitAggregate,
    BAND,
} from './werkplekProfielLogic';
import { useCopy } from '../../i18n/LanguageContext';
import { DIENSTENPROFIEL_ENABLED } from '../../experimental/featureFlag';
import { MIN_TEAM_RESPONSES } from '../../utils/access';
import DienstenLaag from './DienstenLaag';

// Visuele duiding per band (subtiel, binnen het bestaande palet).
const BAND_KLEUR = {
    [BAND.KERN]: 'var(--tof-accent-sage)',
    [BAND.STEUN]: 'var(--tof-accent-sage)',
    [BAND.AANVULLEND]: 'var(--tof-text-muted)',
};
// Zelfde, maar voor de letters in het bandlabel.
const BAND_TEKST = {
    ...BAND_KLEUR,
    [BAND.KERN]: 'var(--tof-accent-sage-text)',
    [BAND.STEUN]: 'var(--tof-accent-sage-text)',
};

// Naam en "soort plek"-illustratie komen uit de copy (i18n); de logica levert
// alleen het type-id. Fallback op het label uit werkplekmix.js.
function typeCopy(COPY, type) {
    const c = COPY.types?.[type.id];
    return {
        label: c?.label || type.label,
        voorbeeldplekken: c?.voorbeeldplekken || [],
    };
}

// `metDiensten`: alleen het teamdashboard zet de laag "Wat dit team naar kantoor
// trekt" aan; het Organisatielandschap (Admin.jsx) gebruikt hetzelfde paneel
// zonder. Met de schakelaar uit verschijnt er niets, ook geen lijn.
export default function WerkplekProfielInline({ aggregate, metDiensten = false }) {
    const isMobile = useIsMobile();
    const { workplaceProfile: COPY } = useCopy();

    const aantallen = aantallenUitAggregate(aggregate);
    const profiel = buildWerkplekProfiel(aantallen);

    if (!profiel.heeftData || profiel.typen.length === 0) {
        return (
            <div style={{ padding: SPACING.lg, fontSize: 13, color: 'var(--tof-text-muted)' }}>
                {COPY.ui.legeStaat}
            </div>
        );
    }

    const signalen = buildGedragssignalen(profiel.typen);

    const [hero, ...overige] = profiel.typen;
    const secundair = overige.filter((t) => t.band !== BAND.AANVULLEND);
    const aanvullend = overige.filter((t) => t.band === BAND.AANVULLEND);

    return (
        <div
            style={{
                display: 'grid',
                gap: isMobile ? SPACING.lg : SPACING['2xl'],
            }}
        >
            <SignatureStatement type={hero} isMobile={isMobile} />

            {secundair.length > 0 ? (
                <SecondaryList items={secundair} isMobile={isMobile} />
            ) : null}

            {aanvullend.length > 0 ? (
                <BelowAverageNote items={aanvullend} isMobile={isMobile} />
            ) : null}

            {signalen.length > 0 ? (
                <SignalenBlok ids={signalen} isMobile={isMobile} />
            ) : null}

            {/* Eigen drempel, ook voor beheerders (die mogen het teambeeld
                onder vijf wel zien). */}
            {metDiensten && DIENSTENPROFIEL_ENABLED && profiel.totaalLeden >= MIN_TEAM_RESPONSES ? (
                <DienstenLaag aggregate={aggregate} />
            ) : null}
        </div>
    );
}

// =========================
// SOORT PLEK — Ruimteboek-bouwstenen als verduidelijking
// =========================

function SoortPlek({ voorbeeldplekken }) {
    const { workplaceProfile: COPY } = useCopy();
    if (!voorbeeldplekken || voorbeeldplekken.length === 0) return null;
    return (
        <div
            style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'baseline',
                gap: SPACING.xs,
                marginTop: 2,
            }}
        >
            <span
                style={{
                    ...TYPE.eyebrow,
                    color: 'var(--tof-text-muted)',
                    marginRight: SPACING.xs,
                }}
            >
                {COPY.ui.illustratieLabel}
            </span>
            {voorbeeldplekken.map((plek) => (
                <span
                    key={plek}
                    style={{
                        fontFamily: 'var(--tof-font-body)',
                        fontSize: 12,
                        lineHeight: 1.4,
                        color: 'var(--tof-text-soft)',
                        background: 'rgba(230, 221, 210, 0.45)',
                        borderRadius: 999,
                        padding: '2px 10px',
                    }}
                >
                    {plek}
                </span>
            ))}
        </div>
    );
}

// =========================
// SIGNATURE STATEMENT — sterkste behoefte
// =========================

function SignatureStatement({ type, isMobile }) {
    const { workplaceProfile: COPY } = useCopy();
    const band = COPY.band[type.band];
    const tc = typeCopy(COPY, type);
    return (
        <div
            style={{
                display: 'grid',
                gap: SPACING.sm,
                paddingBottom: isMobile ? SPACING.md : SPACING.lg,
                borderBottom: '1px solid var(--tof-border)',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: SPACING.md,
                    flexWrap: 'wrap',
                }}
            >
                <div style={{ ...TYPE.eyebrow, color: 'var(--tof-text-muted)' }}>
                    {COPY.ui.heroEyebrow}
                </div>
                {band ? <BandBadge band={type.band} /> : null}
            </div>

            <h3
                style={{
                    margin: 0,
                    fontFamily: 'var(--tof-font-heading)',
                    fontSize: isMobile ? 32 : 'clamp(32px, 3.6vw, 46px)',
                    lineHeight: 1.0,
                    fontWeight: 500,
                    color: 'var(--tof-accent-sage-text)',
                    letterSpacing: '-0.01em',
                }}
            >
                {tc.label}
            </h3>

            {COPY.gebruik[type.id] ? (
                <div
                    style={{
                        fontFamily: 'var(--tof-font-body)',
                        fontSize: 14,
                        lineHeight: 1.55,
                        color: 'var(--tof-text-soft)',
                    }}
                >
                    {COPY.gebruik[type.id]}
                </div>
            ) : null}

            <SoortPlek voorbeeldplekken={tc.voorbeeldplekken} />
        </div>
    );
}

function BandBadge({ band }) {
    const { workplaceProfile: COPY } = useCopy();
    const meta = COPY.band[band];
    if (!meta) return null;
    return (
        <span
            style={{
                fontFamily: 'var(--tof-font-body)',
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: BAND_TEKST[band] || 'var(--tof-text-muted)',
                border: `1px solid ${BAND_KLEUR[band] || 'var(--tof-border)'}`,
                borderRadius: 999,
                padding: '2px 10px',
                whiteSpace: 'nowrap',
            }}
        >
            {meta.label}
        </span>
    );
}

// =========================
// SECONDARY LIST — overige kern/steun
// =========================

function SecondaryList({ items, isMobile }) {
    const { workplaceProfile: COPY } = useCopy();
    // Kernplekken houden hun eigen label. De steunplekken krijgen één
    // tussenkop met de korte uitleg van de band, in plaats van bij elke plek
    // "STEUN" zonder uitleg (A5.9), zoals in de Teaminzicht-PDF.
    const kern = items.filter((item) => item.band === BAND.KERN);
    const steun = items.filter((item) => item.band === BAND.STEUN);

    const grid = (list, withBadge) => (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: isMobile
                    ? '1fr'
                    : 'repeat(2, minmax(0, 1fr))',
                gap: isMobile ? SPACING.md : SPACING.lg,
                columnGap: isMobile ? SPACING.md : SPACING.xl,
            }}
        >
            {list.map((item) => (
                <SecondaryItem key={item.id} type={item} withBadge={withBadge} />
            ))}
        </div>
    );

    return (
        <div style={{ display: 'grid', gap: SPACING.md }}>
            <div style={{ ...TYPE.eyebrow, color: 'var(--tof-text-muted)' }}>
                {COPY.ui.overzichtKop}
            </div>

            {kern.length > 0 ? grid(kern, true) : null}

            {steun.length > 0 ? (
                <>
                    <div
                        style={{
                            fontFamily: 'var(--tof-font-body)',
                            fontSize: 13,
                            fontWeight: 600,
                            color: BAND_TEKST[BAND.STEUN],
                            marginTop: kern.length > 0 ? SPACING.sm : 0,
                        }}
                    >
                        {COPY.band.steun.kort}
                    </div>
                    {grid(steun, false)}
                </>
            ) : null}
        </div>
    );
}

function SecondaryItem({ type, withBadge = true }) {
    const { workplaceProfile: COPY } = useCopy();
    const tc = typeCopy(COPY, type);
    return (
        <div
            style={{
                display: 'grid',
                gap: SPACING.xs + 2,
                paddingBottom: SPACING.sm + 2,
                borderBottom: '1px solid rgba(230, 221, 210, 0.6)',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: SPACING.md,
                }}
            >
                <h4
                    style={{
                        margin: 0,
                        fontFamily: 'var(--tof-font-heading)',
                        fontSize: 20,
                        lineHeight: 1.1,
                        fontWeight: 500,
                        color: 'var(--tof-text)',
                    }}
                >
                    {tc.label}
                </h4>
                {withBadge ? <BandBadge band={type.band} /> : null}
            </div>

            {COPY.gebruik[type.id] ? (
                <div
                    style={{
                        fontFamily: 'var(--tof-font-body)',
                        fontSize: 13,
                        lineHeight: 1.5,
                        color: 'var(--tof-text-soft)',
                    }}
                >
                    {COPY.gebruik[type.id]}
                </div>
            ) : null}

            <SoortPlek voorbeeldplekken={tc.voorbeeldplekken} />
        </div>
    );
}

// =========================
// BELOW AVERAGE NOTE — aanvullende behoeften
// =========================

function BelowAverageNote({ items, isMobile }) {
    const { workplaceProfile: COPY } = useCopy();
    return (
        <div
            style={{
                display: 'grid',
                gap: SPACING.sm,
                paddingTop: isMobile ? SPACING.sm : SPACING.md,
                borderTop: '1px solid var(--tof-border)',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: SPACING.md,
                    flexWrap: 'wrap',
                }}
            >
                <div style={{ ...TYPE.eyebrow, color: 'var(--tof-text-muted)' }}>
                    {COPY.band.aanvullend.kort}
                </div>
                <div
                    style={{
                        fontFamily: 'var(--tof-font-body)',
                        fontSize: 12,
                        color: 'var(--tof-text-muted)',
                    }}
                >
                    {COPY.band.aanvullend.uitleg}
                </div>
            </div>

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile
                        ? '1fr 1fr'
                        : 'repeat(3, minmax(0, 1fr))',
                    columnGap: SPACING.lg,
                    rowGap: SPACING.sm,
                }}
            >
                {items.map((item) => (
                    <div
                        key={item.id}
                        style={{
                            display: 'flex',
                            alignItems: 'baseline',
                            justifyContent: 'space-between',
                            gap: SPACING.sm,
                            paddingBottom: 6,
                            borderBottom: '1px solid rgba(230, 221, 210, 0.4)',
                        }}
                    >
                        <span
                            style={{
                                fontFamily: 'var(--tof-font-body)',
                                fontSize: 14,
                                color: 'var(--tof-text-soft)',
                                fontWeight: 400,
                            }}
                        >
                            {typeCopy(COPY, item).label}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

// =========================
// SIGNALEN — gedragsduiding, secundair
// =========================

function SignalenBlok({ ids, isMobile }) {
    const { workplaceProfile: COPY } = useCopy();
    const signalen = ids
        .map((id) => COPY.signalen[id])
        .filter(Boolean);
    if (signalen.length === 0) return null;

    return (
        <div
            style={{
                display: 'grid',
                gap: SPACING.md,
                paddingTop: isMobile ? SPACING.md : SPACING.lg,
                borderTop: '1px solid var(--tof-border)',
            }}
        >
            <div style={{ ...TYPE.eyebrow, color: 'var(--tof-accent-rose)' }}>
                {COPY.ui.signalenKop}
            </div>

            <div style={{ display: 'grid', gap: SPACING.md }}>
                {signalen.map((s) => (
                    <div
                        key={s.titel}
                        style={{
                            display: 'grid',
                            gap: SPACING.xs,
                            paddingLeft: SPACING.md,
                            borderLeft: '2px solid var(--tof-accent-rose)',
                        }}
                    >
                        <div
                            style={{
                                fontFamily: 'var(--tof-font-heading)',
                                fontSize: 18,
                                lineHeight: 1.15,
                                fontWeight: 500,
                                color: 'var(--tof-text)',
                            }}
                        >
                            {s.titel}
                        </div>
                        <div
                            style={{
                                fontFamily: 'var(--tof-font-body)',
                                fontSize: 13,
                                lineHeight: 1.55,
                                color: 'var(--tof-text-soft)',
                            }}
                        >
                            {s.tekst}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
